/**
 * Pure helpers for the Full response reader (spec 29 §8).
 *
 * Kept out of the components so the workflow-margin rules — who signed a
 * section off, and whether the reader may still edit it — are testable
 * without rendering the page.
 */

import DOMPurify from 'dompurify'
import { format } from 'date-fns'
import type { SectionAssignment } from '@/stores/section-assignments-store'
import type { BidArtifact } from '@/lib/api'

export interface ProvenanceLine {
  text: string
  /** Section content changed after the decision it reports. */
  stale: boolean
}

const day = (iso: string) => format(new Date(iso), 'd MMM')

/**
 * One line under the status pill (spec 29 §0.6.7). Staleness is the timestamp
 * comparison §0.3 allows until content-revision provenance is justified.
 */
export function provenanceFor(
  assignment: SectionAssignment | undefined,
  sectionUpdatedAt: string | null,
): ProvenanceLine | null {
  if (!assignment) return null

  if (assignment.collaborationStatus === 'approved') {
    const round = [...assignment.reviewRounds].reverse().find((r) => r.decision === 'approved')
    if (!round) return null
    const at = round.decidedAt ?? round.createdAt
    return {
      text: `Approved by ${round.reviewerName ?? 'reviewer'} · ${day(at)}`,
      stale: sectionUpdatedAt !== null && sectionUpdatedAt > at,
    }
  }

  if (assignment.collaborationStatus === 'complete') {
    const at = assignment.collaborationStatusChangedAt
    const who = assignment.fullName || assignment.email || 'writer'
    return {
      text: `Completed by ${who} · ${day(at)}`,
      stale: sectionUpdatedAt !== null && sectionUpdatedAt > at,
    }
  }

  return null
}

/**
 * Mirrors SectionEditorPage's canWrite: read-only user → no; frozen → no;
 * owner → yes; unassigned or mine → yes.
 */
export function canEditSection(input: {
  artifactStatus: BidArtifact['status'] | undefined
  isOwner: boolean
  assignment: SectionAssignment | undefined
  currentUserId: string | undefined
  /** An org viewer, or a member who is not on the bid (`useBidReadOnly`). */
  isReadOnly?: boolean
}): boolean {
  const { artifactStatus, isOwner, assignment, currentUserId, isReadOnly = false } = input
  if (isReadOnly) return false
  if (artifactStatus === 'finalized' || artifactStatus === 'pending_review') return false
  if (isOwner) return true
  if (!assignment || assignment.userId === null) return true
  return assignment.userId === currentUserId
}

/**
 * Drafted HTML is prose, not a whole document — the default HTML profile keeps
 * headings, lists and tables and drops scripts, styles and event handlers.
 */
export function sanitizeResponseHtml(html: string): string {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })
}

export function sectionDomId(sectionKey: string): string {
  return `full-response-section-${sectionKey}`
}
